import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import {
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@nextui-org/react";
import dayjs from "../../utils/dayjsConfig";
import { getPopUps, dismissPopUp } from "../../connectors/popUpsApi";
import type {
  PopUpResponseDTO,
  PopUpBodyMaintenance,
  PopUpBodyNews,
  PopUpBodyBidding,
} from "../../types/types";
import { useAppSelector } from "../../store/hooks";
import useScreenSize from "../../hooks/useScreenSize";
import CustomModal from "../CustomModal/CustomModal";
import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";
import NewsIcon from "../../assets/images/illustrations/news.svg?react";
import MaintenanceIcon from "../../assets/images/illustrations/maintenance.svg?react";
import styles from "./PopUpModal.module.scss";

const ARGENTINA_TZ = "America/Argentina/Buenos_Aires";
const SESSION_KEY_PREFIX = "popup_seen_session_";

const isMaintenanceBody = (
  body: PopUpResponseDTO["body"],
): body is PopUpBodyMaintenance => "startTime" in body && "endTime" in body;

const isNewsBody = (body: PopUpResponseDTO["body"]): body is PopUpBodyNews =>
  "description" in body;

const isBiddingBody = (
  body: PopUpResponseDTO["body"],
): body is PopUpBodyBidding => "emission" in body;

const formatTime = (iso: string) => {
  return dayjs(iso).tz(ARGENTINA_TZ).format("HH:mm");
};

const PopUpModal = () => {
  const { user } = useAppSelector((state) => state.authentication);
  const { isMobile } = useScreenSize();

  const queryClient = useQueryClient();
  const isNative = Capacitor.isNativePlatform();
  const loginSessionId = useRef("");

  useEffect(() => {
    if (!user?.email) return;

    const storage = isNative ? localStorage : sessionStorage;
    const sessionKey = `${SESSION_KEY_PREFIX}session_${user.email.replace(/[^a-zA-Z0-9]/g, "_")}`;

    let id = storage.getItem(sessionKey);
    if (!id) {
      id = dayjs().tz(ARGENTINA_TZ).toISOString();
      storage.setItem(sessionKey, id);
    }
    loginSessionId.current = id;

    return () => {
      storage.removeItem(sessionKey);
      loginSessionId.current = "";
    };
  }, [user?.email]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [visiblePopUps, setVisiblePopUps] = useState<PopUpResponseDTO[]>([]);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const { data } = useQuery({
    queryKey: ["popups", "published"],
    queryFn: () => getPopUps({ isPublished: true, size: 50 }),
    staleTime: 1000 * 60 * 5,
    enabled: !!user?.email,
  });

  const { mutate: dismiss } = useMutation({
    mutationFn: (id: number) => dismissPopUp(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["popups"] });
    },
  });

  const getSessionKey = (id: number) => {
    if (!user?.email) return "";
    const sanitized = user.email.replace(/[^a-zA-Z0-9]/g, "_");
    return `${SESSION_KEY_PREFIX}${sanitized}_${id}`;
  };

  const hasSeenThisSession = (id: number) => {
    const key = getSessionKey(id);
    if (!key) return false;
    const storage = isNative ? localStorage : sessionStorage;
    return storage.getItem(key) === loginSessionId.current;
  };

  const markSeenThisSession = (id: number) => {
    const key = getSessionKey(id);
    if (!key) return;
    const storage = isNative ? localStorage : sessionStorage;
    storage.setItem(key, loginSessionId.current);
  };

  useEffect(() => {
    if (!data?.content || !user?.email) return;

    const now = dayjs().tz(ARGENTINA_TZ);
    const popups = data.content.filter((popup) => {
      if (
        popup.deadline &&
        dayjs(popup.deadline).tz(ARGENTINA_TZ).isBefore(now)
      ) {
        return false;
      }
      if (
        popup.frequency === "ONCE_PER_SESSION" ||
        popup.frequency === "UNTIL_TURNS_OFF"
      ) {
        return !hasSeenThisSession(popup.id);
      }
      return true;
    });

    if (popups.length > 0) {
      setVisiblePopUps(popups);
      setCurrentIndex(0);
      setIsOpen(true);
    }
  }, [data?.content, user?.email]);

  const currentPopUp = visiblePopUps[currentIndex];

  const handleClose = () => {
    if (!currentPopUp) return;

    if (currentPopUp.frequency === "UNTIL_TURNS_OFF" && dontShowAgain) {
      dismiss(currentPopUp.id);
    } else if (
      currentPopUp.frequency === "ONCE_PER_SESSION" ||
      currentPopUp.frequency === "UNTIL_TURNS_OFF"
    ) {
      markSeenThisSession(currentPopUp.id);
    } else {
      dismiss(currentPopUp.id);
    }

    setDontShowAgain(false);
    const next = currentIndex + 1;
    if (next < visiblePopUps.length) {
      setCurrentIndex(next);
    } else {
      setIsOpen(false);
    }
  };

  const renderBody = (popup: PopUpResponseDTO) => {
    const { body, type } = popup;

    if (type === "MAINTENANCE" && isMaintenanceBody(body)) {
      return (
        <p className={styles.description}>
          Entre {formatTime(body.startTime)} y {formatTime(body.endTime)}.{" "}
          <br /> Te agradecemos por tu comprensión.
        </p>
      );
    }

    if ((type === "NEWS" || type === "NEWS_HYPERLINK") && isNewsBody(body)) {
      return <p className={styles.description}>{body.description}</p>;
    }

    if (type === "BIDDING" && isBiddingBody(body)) {
      return (
        <div className={styles.biddingBody}>
          {body.emission && (
            <p className={styles.biddingField}>
              <strong>Emisión:</strong> {body.emission}
            </p>
          )}
          {body.term && (
            <p className={styles.biddingField}>
              <strong>Plazo:</strong> {body.term}
            </p>
          )}
          {body.integration && (
            <p className={styles.biddingField}>
              <strong>Integración:</strong> {body.integration}
            </p>
          )}
          <p className={styles.biddingField}>
            <strong>Amortización:</strong> {body.amortization}
          </p>
          {body.initialGuide && (
            <p className={styles.biddingField}>
              <strong>Guía inicial:</strong> {body.initialGuide}
            </p>
          )}
          {body.objective && (
            <p className={styles.biddingField}>
              <strong>Objetivo:</strong> {body.objective}
            </p>
          )}
          {body.riskRating && (
            <p className={styles.biddingField}>
              <strong>Calificación de riesgo:</strong> {body.riskRating}
            </p>
          )}
        </div>
      );
    }

    return null;
  };

  const renderIcon = (popup: PopUpResponseDTO) => {
    switch (popup.type) {
      case "MAINTENANCE":
        return (
          <div className={styles.imageWrapper}>
            <MaintenanceIcon className={styles.icon} />
          </div>
        );
      default:
        return (
          <div className={styles.imageWrapper}>
            <NewsIcon className={styles.icon} />
          </div>
        );
    }
  };

  if (!currentPopUp) return null;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      isLargerContent
      classNames={
        isMobile
          ? undefined
          : { body: "mx-10 my-0 p-0 overflow-y-auto max-h-[55vh]" }
      }
    >
      <ModalContent>
        <ModalHeader />
        <ModalBody className={styles.container}>
          {renderIcon(currentPopUp)}
          <h1>{currentPopUp.title}</h1>
          {renderBody(currentPopUp)}
        </ModalBody>
        <ModalFooter>
          <div className={styles.footerContent}>
            {currentPopUp.frequency === "UNTIL_TURNS_OFF" && (
              <div className={styles.checkboxContainer}>
                <Checkbox
                  isSelected={dontShowAgain}
                  onChange={() => setDontShowAgain(!dontShowAgain)}
                >
                  No volver a mostrar
                </Checkbox>
              </div>
            )}
            <div className={styles.buttonsContainer}>
              {(currentPopUp.type === "NEWS_HYPERLINK" ||
                currentPopUp.type === "BIDDING") &&
              currentPopUp.link ? (
                <>
                  <Button
                    text="Cerrar"
                    onPress={handleClose}
                    variant="secondary"
                  />
                  <Button
                    text={currentPopUp.buttonText ?? "Ver más"}
                    onPress={() => {
                      window.open(currentPopUp.link, "_blank", "noreferrer");
                      handleClose();
                    }}
                  />
                </>
              ) : (
                <Button
                  text={
                    currentPopUp.type === "MAINTENANCE" ||
                    currentPopUp.type === "NEWS"
                      ? "Cerrar"
                      : (currentPopUp.buttonText ?? "Cerrar")
                  }
                  onPress={handleClose}
                />
              )}
            </div>
          </div>
        </ModalFooter>
      </ModalContent>
    </CustomModal>
  );
};

export default PopUpModal;
